import { useContext, useEffect, useState } from 'react';
import { ServiceResponse } from '../../../app/services/shared/interfaces';
import DefaultCard from '../../../components/default/DefaultCard';
import ButtonCreate from '../../../components/buttons/ButtonCreate';
import LazyLoading from '../../../components/LazyLoading';
import DefaultModal from '../../../components/default/DefaultModal';
import useReactConfirmAlert from '../../../hooks/useReactConfirmAlert';
import { AppContext } from '../../../contexts/AppContext';
import { toast } from 'react-toastify';
import useDeductionRateService from '../../../app/services/hooks/useDeductionRateService';
import DeductionRateCreate from '../subpages/DeductionRateCreate';
import { DeductionRateDataForm, DeductionRateFilters } from '../../../app/models/DeductionRate';
import DeductionRateDataTable from './tables/DeductionRateDataTable';
import DeductionRateEdit from '../subpages/DeductionRateEdit';


interface Props {
    deductionId: number;
}

const DeductionRateContainer = ({ deductionId }: Props) => {
    const { showLoading, hideLoading, changeAnimation } = useContext(AppContext);
    const { getAllDeductionRatesByDeduction, fetchingGetDeductionRatesByDeduction, deleteDeductionRate } = useDeductionRateService();

    const [showingEdit, setShowingEdit] = useState(false);
    const [showingCreate, setShowingCreate] = useState(false);
    const [deductionRateId, setDeductionRateId] = useState<number>(-1);
    const [deductionRates, setDeductionRates] = useState<DeductionRateDataForm[]>([]);
    const [totalRows, setTotalRows] = useState<number>(0);

    const [filter, setFilter] = useState<DeductionRateFilters>({
        page: 1,
        per_page: 10,
        sort: 'id',
        order: 'desc'
    });

    const showCreate = () => {
        setShowingCreate(true);
    };

    const hideCreate = () => {
        setShowingCreate(false);
    };

    const showEdit = (id: number) => {
        setShowingEdit(true);
        setDeductionRateId(id);
    };

    const hideEdit = () => {
        setShowingEdit(false);
        setDeductionRateId(-1);
    };

    useEffect(() => {
        reloadTable();
    }, [filter.page, filter.per_page, filter.sort, filter.order]);

    const destroy = (deductionRateId: number) => {
        const _text = 'Está a punto de eliminar la tarifa de Afp #' + deductionRateId;

        useReactConfirmAlert().requestConfirmation({
            title: '¿Estás seguro?',
            message: _text,
            buttons: {
                confirmButton: {
                    label: 'Si, confirmar',
                    onClick: () => {
                        if (showLoading) showLoading('loading', 'Eliminando tarifa de Deducción...');
                        deleteDeductionRate(deductionRateId, {
                            onSuccess: (response: ServiceResponse) => {
                                if (hideLoading) hideLoading();

                                useReactConfirmAlert().successAlert({
                                    title: 'Éxito',
                                    message: response.message
                                });

                                reloadTable();
                            },
                            onError: (response: ServiceResponse) => {
                                if (hideLoading) hideLoading();

                                useReactConfirmAlert().errorAlert({
                                    title: 'Error',
                                    message: response.message
                                });
                            }
                        });
                    }
                },
                cancelButton: {
                    label: 'No, cancelar',
                    onClick: () => {
                        setTimeout(() => {
                            useReactConfirmAlert().infoAlert({
                                title: 'Cancelado',
                                message: 'La tarifa de Afp no se ha eliminado.'
                            });
                        }, 0);
                    }
                }
            }
        });
    };

    const reloadTable = () => {
        getAllDeductionRatesByDeduction(deductionId, {
            onSuccess: (response: ServiceResponse) => {
                setDeductionRates(response.data.deduction_rates)
                setTotalRows(response.data.total_rows);
            },
            onError: (response: ServiceResponse) => {
                toast.error(response.message);
            }
        });
    };

    return (
        <>
            <DefaultCard>
                {!fetchingGetDeductionRatesByDeduction ? (
                    <div className="row">
                        <div className="col-12 mb-2">
                            <ButtonCreate
                                title="Nueva tarifa de Deducción"
                                callbackCreate={showCreate}
                            />
                        </div>
                        <div className="col-12">
                            {deductionRates ? (
                                <DeductionRateDataTable
                                    deductionRates={deductionRates}
                                    edit={showEdit}
                                    destroy={destroy}
                                    loading={fetchingGetDeductionRatesByDeduction}
                                    totalRows={totalRows}
                                    filter={filter}
                                    setFilter={setFilter}
                                />
                            ) : null}
                        </div>
                    </div>
                ) : (
                    <LazyLoading height="300" />
                )}
            </DefaultCard>
            {showingCreate ? (
                <DefaultModal
                    show={showingCreate}
                    handleClose={hideCreate}
                    title="Crear Tarifa de deducción"
                    backdrop={true}
                    showFooter={false}>
                    <DeductionRateCreate
                        deductionId={deductionId}
                        onSaved={() => {
                            reloadTable();
                            hideCreate();
                        }}
                        onCancel={hideCreate}
                        onError={hideCreate}
                    />
                </DefaultModal>
            ) : null} 

            {showingEdit ? (
                <DefaultModal
                    show={showingEdit}
                    handleClose={hideEdit}
                    title="Editar Tarifa de Deducción"
                    backdrop={true}
                    showFooter={false}>
                    <DeductionRateEdit
                        onSaved={() => {
                            reloadTable();
                            hideEdit();
                        }}
                        onCancel={hideEdit}
                        onError={hideEdit}
                        deductionRateId={deductionRateId}
                    />
                </DefaultModal>
            ) : null}
        </>
    );
};

export default DeductionRateContainer;
